import {
  Injectable,
  computed,
  inject,
  signal
} from '@angular/core';
import { Observable, tap } from 'rxjs';
import dayjs from 'dayjs';

import { AuthState } from '../users';
import { isServiceItemUsable } from '../service-items/utils';

import {
  ApiResponse,
  Avatar,
  Reward,
  UserReward,
  CheckedUser,
  RewardStatus,
  UserRewardStatus
} from './daily-rewards.entity';
import { DailyRewardsService } from './daily-rewards.service';

@Injectable({
  providedIn: 'root'
})
export class DailyRewardsState {
  private readonly authState = inject(AuthState);
  private readonly dailyRewardsService = inject(DailyRewardsService);

  public readonly userRewards = signal<UserReward[]>([]);
  public readonly nextRollAt = signal<string|null>(null);
  public readonly avatars = signal<Avatar[]>([]);
  public readonly avatarCheckedUser = signal<CheckedUser|null>(null);
  public readonly selectedAvatarId = signal<number|null>(null);

  private readonly _rewards = signal<Reward[]>([]);

  public readonly rewards = computed(() => {
    const result: Reward[] = [];
    const userRewards = this.userRewards();
    const nextReward = this.nextUserReward();
    const conditionsMet = this.conditionsMet();

    this._rewards().forEach((reward: Reward) => {
      const userReward = userRewards.find(row => row.reward.id === reward.id);
      const isNext = nextReward.reward.id === reward.id;

      let status = RewardStatus.DEFAULT;

      if(userReward) {
        const isUsable: boolean = isServiceItemUsable(userReward.reward.serviceItem.type);

        if(userReward.status === UserRewardStatus.TAKEN && isUsable) {
          status = RewardStatus.USABLE;
        } else {
          status = RewardStatus.TAKEN;
        }
      } else if(conditionsMet) {
        if(!reward.depositConditionMet) {
          status = RewardStatus.UNAVAILABLE;
        } else if(isNext) {
          if(nextReward.nextRewardAt) {
            status = RewardStatus.AVAILABLE_SOON;
          } else {
            status = RewardStatus.AVAILABLE;
          }
        }
      }

      result.push({
        ...reward,
        userReward,
        status,
        nextRewardAt: isNext ? nextReward.nextRewardAt : null,
        isNext
      });
    });

    return result;
  });

  public readonly nextUserReward = computed(() => {
    const rewards = this._rewards();
    const userRewards = this.userRewards();

    const userRewardsCount = userRewards.length;
    const nextRewardDay = Math.min(rewards.length, userRewardsCount + 1);
    const nextReward = rewards.find((reward: Reward) => reward.day === nextRewardDay)!;

    let nextRewardAt: string|null = null;

    if(userRewardsCount > 0) {
      const lastReward = userRewards.find(userReward => userReward.reward.day === nextRewardDay - 1);

      if(lastReward) {
        const nextRewardTimestamp = dayjs.utc(lastReward.createdAt)
          .hour(0)
          .minute(0)
          .second(0)
          .add(1, 'day');

        if(nextRewardTimestamp.unix() > dayjs().unix()) {
          nextRewardAt = nextRewardTimestamp.toISOString();
        }
      }
    }

    return {
      reward: nextReward,
      nextRewardAt
    };
  });

  public readonly progress = computed(() => {
    // TODO CSFP-577 расчет позиции прогрессбара
    const userRewards = this.userRewards();
    const rewards = this._rewards();

    return userRewards.length / rewards.length * 100;
  });

  public readonly conditionsMet = computed(() => {
    const checkedUser = this.avatarCheckedUser();

    return !!checkedUser && checkedUser.daysPassed >= 1;
  });

  public readonly selectedAvatar = computed(() => {
    const selectedAvatarId = this.selectedAvatarId();

    return this.avatars().find(avatar => avatar.id === selectedAvatarId);
  });

  load(): Observable<ApiResponse.RewardsList> {
    return this.dailyRewardsService.getRewards().pipe(
      tap((response: ApiResponse.RewardsList) => {
        this._rewards.set(response.rewards);
        this.userRewards.set(response.userRewards);
        this.nextRollAt.set(response.nextRollAt);
        this.avatars.set(response.avatars);
        this.avatarCheckedUser.set(response.avatarCheckedUser);
        this.selectedAvatarId.set(response.avatarCheckedUser?.avatar.id || null);
      })
    );
  }

  takeReward(id: number): Observable<ApiResponse.TakenReward> {
    return this.dailyRewardsService.takeReward(id).pipe(
      tap((response: ApiResponse.TakenReward) => {
        this.userRewards.update((userRewards) => [...userRewards, response.userReward]);
      })
    );
  }

  checkAvatar(): Observable<any> {
    return this.dailyRewardsService.checkSteamPhoto().pipe(
      tap((response: ApiResponse.CheckSteamPhoto) => {
        this.avatarCheckedUser.set(response.checkedUser);
        this.selectedAvatarId.set(response.checkedUser.avatar.id);
        this.authState.patchUser('photoUrl', response.steamPhoto);
      })
    );
  }

  markRewardAsUsed(id: number): void {
    this.userRewards.update((userRewards) => {
      const index = userRewards.findIndex((row) => row.id === id);

      userRewards[index] = {
        ...userRewards[index],
        status: UserRewardStatus.USED
      };

      return [...userRewards];
    });
  }

  setSelectedAvatarId(id: number): void {
    this.selectedAvatarId.set(id);
  }

  reset(): void {
    this.userRewards.set([]);
    this.nextRollAt.set(null);
    this.avatars.set([]);
    this.avatarCheckedUser.set(null);
    this.selectedAvatarId.set(null);
  }
}
